import { useLayoutEffect, useMemo, useRef } from 'react'
import { formatText } from '../../../../utils/formatText'
import './ParticipantList.css'

interface Participant {
	id?: string
	name: string
	points: number
	self: boolean
}

const MAX_NICKNAME_LENGHT = 18

export const ParticipantList = ({ participants }: { participants: Participant[] }) => {
	const itemRefs = useRef(new Map<string, HTMLDivElement>())
	const prevPositionsRef = useRef(new Map<string, DOMRect>())

	const getKey = (participant: Participant) => participant.id ?? participant.name

	const sortedParticipants = useMemo(() => {
		return [...participants].sort((a, b) => b.points - a.points)
	}, [participants])

	useLayoutEffect(() => {
		const nextPositions = new Map<string, DOMRect>()

		sortedParticipants.forEach((participant) => {
			const key = getKey(participant)
			const node = itemRefs.current.get(key)

			if (!node) return

			const nextRect = node.getBoundingClientRect()
			nextPositions.set(key, nextRect)

			const prevRect = prevPositionsRef.current.get(key)
			if (!prevRect) return

			const deltaY = prevRect.top - nextRect.top
			if (deltaY === 0) return

			node.style.transition = 'none'
			node.style.transform = `translateY(${deltaY}px)`

			node.getBoundingClientRect()

			requestAnimationFrame(() => {
				node.style.transition = 'transform 450ms cubic-bezier(0.16, 1, 0.3, 1)'
				node.style.transform = 'translateY(0)'
			})
		})

		prevPositionsRef.current = nextPositions
	}, [sortedParticipants])

	const formatPoints = (points: number) => {
		if (points >= 1000000000) {
			return `${Number((points / 1000000000).toFixed(3))}B`
		}
		if (points >= 1000000) {
			return `${Number((points / 1000000).toFixed(3))}M`
		}
		if (points >= 10000) {
			return `${Number((points / 1000).toFixed(2))}K`
		}
		return points.toString()
	}

	return (
		<div className='participant-list-wrapper'>
			<div className='participant-list'>
				{sortedParticipants.map((participant) => {
					const key = getKey(participant)

					return (
						<div
							className="participant"
							key={key}
							ref={(node) => {
								if (node) itemRefs.current.set(key, node)
								else itemRefs.current.delete(key)
							}}
						>
							<div className={`participant-name ${participant.self ? 'self' : ''}`}>
								<p>{formatText(participant.name, MAX_NICKNAME_LENGHT)}</p>
							</div>

							<div className={`participant-points ${participant.self ? 'self' : ''}`}>
								<p>{formatPoints(participant.points)}</p>
							</div>
						</div>
					)
				})}
			</div>
		</div>
	)
}